import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Search, RefreshCw, Users, Eye, Power, FileText, X, Check, Loader2, Trash2, Crown, Pencil } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtMoney = (n, c = "EUR") => new Intl.NumberFormat("pt-PT", { style: "currency", currency: c }).format(Number(n || 0));

const StatePill = ({ state }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${
    state === "N" ? "bg-[#7A2021]/15 text-[#7A2021] border border-[#7A2021]/30"
                  : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
  }`}>{state === "N" ? "DESATIVADA" : "ATIVA"}</span>
);

const SUBSCRIPTION_LABEL = {
  standard: { label: "Standard", cls: "bg-white/5 text-[#A29F9B] border border-white/10" },
  premium: { label: "Premium", cls: "bg-[#C5A880]/15 text-[#C5A880] border border-[#C5A880]/40" },
  stripe: { label: "Stripe", cls: "bg-indigo-500/15 text-indigo-400 border border-indigo-500/40" },
  eupago: { label: "EuPago", cls: "bg-teal-500/15 text-teal-400 border border-teal-500/40" },
  founders_free: { label: "Founder", cls: "bg-[#7B2D26]/15 text-[#7B2D26] border border-[#7B2D26]/40" },
  admin_grant: { label: "Oferta admin", cls: "bg-purple-500/15 text-purple-400 border border-purple-500/40" },
};

const SubscriptionPill = ({ kind, status }) => {
  // Prefer the more-specific subscription_status when set (e.g. admin_grant),
  // otherwise fall back to the computed `kind`.
  const key = SUBSCRIPTION_LABEL[status] ? status : kind || "standard";
  const cfg = SUBSCRIPTION_LABEL[key] || SUBSCRIPTION_LABEL.standard;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${cfg.cls}`}>
      {(key === "premium" || key === "founders_free" || key === "stripe") && <Crown className="w-3 h-3" />}
      {cfg.label}
    </span>
  );
};

const AdminUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);   // full user detail
  const [invoices, setInvoices] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [acting, setActing] = useState(false);
  const [purgeModal, setPurgeModal] = useState(null);  // { user, confirmText, keepInvoices }
  const [premiumModal, setPremiumModal] = useState(null);  // { current_until, new_until_iso, reason }
  const [foundersStats, setFoundersStats] = useState(null);  // {active, ends_at, would_update}
  const [backfilling, setBackfilling] = useState(false);

  // Probe the founders promo state + how many users would be affected.
  // Pure read (dry_run=true); safe to call on every mount/refresh.
  const probeFounders = useCallback(async () => {
    try {
      const [promo, dry] = await Promise.all([
        client.get("/promo/founders"),
        client.post("/admin/founders/backfill?dry_run=true"),
      ]);
      setFoundersStats({
        active: !!promo.data?.active,
        ends_at: promo.data?.ends_at,
        days_left: promo.data?.days_left,
        would_update: dry.data?.would_update ?? 0,
      });
    } catch {
      setFoundersStats(null);
    }
  }, []);

  const runFoundersBackfill = async () => {
    if (!foundersStats?.would_update) return;
    if (!window.confirm(
      `Vais aplicar Premium até ${new Date(foundersStats.ends_at).toLocaleDateString("pt-PT")} ` +
      `a ${foundersStats.would_update} utilizador(es). Continuar?`
    )) return;
    setBackfilling(true);
    try {
      const r = await client.post("/admin/founders/backfill?dry_run=false");
      toast.success(`${r.data.modified} utilizador(es) actualizado(s) para Founder.`);
      await probeFounders();
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao aplicar promo.");
    } finally { setBackfilling(false); }
  };

  useEffect(() => {
    if (!user) return;
    if (!user.is_admin) { navigate("/"); return; }
  }, [user, navigate]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (q.trim()) params.q = q.trim();
      if (stateFilter !== "all") params.state = stateFilter;
      const r = await client.get("/admin/users", { params });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao listar utilizadores.");
    } finally { setLoading(false); }
  }, [page, q, stateFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { probeFounders(); }, [probeFounders]);

  const openDetail = async (row) => {
    setDetail({ loading: true, id: row.id });
    setInvoices(null);
    setLoadingDetail(true);
    try {
      const [fullRes, invRes] = await Promise.all([
        client.get(`/admin/users/${row.id}/full`),
        client.get(`/admin/users/${row.id}/invoices`),
      ]);
      setDetail(fullRes.data);
      setInvoices(invRes.data.invoices || []);
    } catch (err) {
      toast.error("Falha a carregar detalhe.");
      setDetail(null);
    } finally { setLoadingDetail(false); }
  };

  const changeState = async (targetState) => {
    if (!detail) return;
    const verb = targetState === "N" ? "desativar" : "reativar";
    if (!window.confirm(`Tens a certeza que queres ${verb} esta conta?`)) return;
    setActing(true);
    try {
      const r = await client.put(`/admin/users/${detail.id}/state`, { state: targetState, restore_email: true });
      setDetail(r.data.user);
      toast.success(`Conta ${targetState === "N" ? "desativada" : "reativada"}.`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao alterar estado.");
    } finally { setActing(false); }
  };

  const executePurge = async () => {
    if (!purgeModal || !detail) return;
    if (purgeModal.confirmText !== detail.id) {
      toast.error("O ID não coincide.");
      return;
    }
    setActing(true);
    try {
      const r = await client.delete(`/admin/users/${detail.id}/purge`, {
        data: { confirm: detail.id, keep_invoices: purgeModal.keepInvoices },
      });
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(`Utilizador eliminado. ${total} registos removidos.`);
      setPurgeModal(null);
      setDetail(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao eliminar.");
    } finally { setActing(false); }
  };

  const savePremium = async () => {
    if (!premiumModal || !detail) return;
    const reason = (premiumModal.reason || "").trim();
    if (reason.length < 3) {
      toast.error("Indica uma justificação (mínimo 3 caracteres).");
      return;
    }
    setActing(true);
    try {
      // Convert "" / undefined into null = revoke premium; otherwise convert
      // the YYYY-MM-DD picker value into an end-of-day ISO timestamp so the
      // user has the FULL chosen day of access.
      const raw = (premiumModal.new_until || "").trim();
      const new_until = raw ? `${raw}T23:59:59+00:00` : null;
      const r = await client.put(`/admin/users/${detail.id}/premium`, {
        premium_until: new_until,
        reason,
      });
      toast.success(new_until ? "Premium actualizado." : "Premium revogado.");
      setDetail(r.data.user);
      setPremiumModal(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao atualizar premium.");
    } finally { setActing(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="admin-users-page">
      <header className="border-b border-white/5 sticky top-0 bg-[#0C0E12]/95 backdrop-blur z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[#A29F9B] hover:text-[#C5A880]">
            <ArrowLeft className="w-4 h-4" /> Voltar ao painel
          </Link>
          <div className="inline-flex items-center gap-2 text-sm text-[#C5A880]">
            <Users className="w-4 h-4" /> Gestão de utilizadores
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <h1 className="font-display text-3xl md:text-4xl mb-2">Utilizadores</h1>
        <p className="text-sm text-[#A29F9B] mb-8">
          Lista completa — ativos e desativados. Podes ver detalhe, histórico de faturas e mudar o estado.
        </p>

        {/* Founders backfill banner — only shows when promo active AND eligible users exist */}
        {foundersStats?.active && foundersStats.would_update > 0 && (
          <div className="bg-gradient-to-br from-[#C5A880]/15 via-[#161920] to-[#161920] border border-[#C5A880]/40 rounded-2xl p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4"
               data-testid="founders-backfill-card">
            <div className="w-11 h-11 rounded-full bg-[#C5A880]/20 border border-[#C5A880]/40 flex items-center justify-center shrink-0">
              <Crown className="w-5 h-5 text-[#C5A880]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg text-[#F5F2EE]">Promo Founders activa · resta {foundersStats.days_left} dia(s)</h3>
              <p className="text-sm text-[#A29F9B] mt-0.5">
                <strong className="text-[#F5F2EE]">{foundersStats.would_update}</strong> utilizador(es) sem Premium poderiam receber acesso gratuito
                até {new Date(foundersStats.ends_at).toLocaleDateString("pt-PT")}.
                Subscritores Stripe activos não são alterados.
              </p>
            </div>
            <button onClick={runFoundersBackfill} disabled={backfilling}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C5A880] text-[#0C0E12] hover:bg-[#D4B690] text-sm font-semibold whitespace-nowrap disabled:opacity-50"
              data-testid="founders-backfill-apply">
              {backfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
              Aplicar a todos ({foundersStats.would_update})
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A29F9B]" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Procurar por nome ou email..."
              className="w-full bg-[#161920] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:border-[#C5A880]/50 focus:outline-none"
              data-testid="users-search" />
          </div>
          <div className="flex gap-2">
            {[{v:"all",l:"Todos"},{v:"S",l:"Ativos"},{v:"N",l:"Desativados"}].map((o) => (
              <button key={o.v} onClick={() => { setStateFilter(o.v); setPage(1); }}
                className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                  stateFilter === o.v ? "border-[#C5A880] text-[#C5A880] bg-[#C5A880]/10" : "border-white/10 text-[#A29F9B] hover:border-white/20"
                }`}
                data-testid={`filter-state-${o.v}`}>{o.l}</button>
            ))}
            <button onClick={fetchUsers} className="px-3 py-2 rounded-lg border border-white/10 hover:border-[#C5A880]/40 transition-colors" data-testid="users-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Table */}
        <section className="bg-[#161920] border border-white/5 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 text-xs text-[#A29F9B] border-b border-white/5 flex justify-between">
            <span>{total.toLocaleString("pt-PT")} resultado(s)</span>
            <span>Página {page} / {totalPages}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#A29F9B] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Nome</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Estado</th>
                  <th className="text-left px-5 py-3">Registo</th>
                  <th className="text-left px-5 py-3">Subscrição</th>
                  <th className="text-left px-5 py-3">Válido até</th>
                  <th className="text-right px-5 py-3">Ação</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-[#A29F9B]">Nenhum resultado.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`user-row-${r.id}`}>
                    <td className="px-5 py-3 text-[#F5F2EE]">{r.name || "—"}</td>
                    <td className="px-5 py-3 text-[#DCD7D2] font-mono text-xs">{r.email}</td>
                    <td className="px-5 py-3"><StatePill state={r.profile_state || "S"} /></td>
                    <td className="px-5 py-3 text-[#A29F9B] text-xs">{fmtDate(r.created_at)}</td>
                    <td className="px-5 py-3"><SubscriptionPill kind={r.subscription_kind} status={r.subscription_status} /></td>
                    <td className="px-5 py-3 text-[#A29F9B] text-xs">{r.premium_until ? fmtDate(r.premium_until) : "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => openDetail(r)} className="inline-flex items-center gap-1 text-[#C5A880] hover:underline text-xs" data-testid={`view-${r.id}`}>
                        <Eye className="w-3.5 h-3.5" /> Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/5 text-xs">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 rounded border border-white/10 disabled:opacity-30">Anterior</button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-white/10 disabled:opacity-30">Seguinte</button>
            </div>
          )}
        </section>
      </main>

      {/* Detail drawer / modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-8" onClick={() => setDetail(null)}>
          <div className="bg-[#161920] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-detail-modal">
            <div className="sticky top-0 bg-[#161920] border-b border-white/5 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-[#A29F9B]">Utilizador</div>
                <h2 className="font-display text-xl">{detail.name || detail.email}</h2>
              </div>
              <button onClick={() => setDetail(null)} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>

            {loadingDetail ? (
              <div className="p-12 text-center text-[#A29F9B]"><Loader2 className="w-5 h-5 animate-spin inline" /> A carregar...</div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Core */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Field label="ID" value={detail.id} mono />
                  <Field label="Email" value={detail.email} mono />
                  <Field label="Estado" value={<StatePill state={detail.profile_state || "S"} />} />
                  <Field label="Admin" value={detail.is_admin ? "Sim" : "Não"} />
                  <Field label="Onboarding" value={detail.onboarding_complete ? "Completo" : "Pendente"} />
                  <Field label="Subscrição"
                         value={<SubscriptionPill kind={detail.subscription_kind} status={detail.subscription_status} />} />
                  <Field label="Premium até"
                         value={
                           <span className="inline-flex items-center gap-2">
                             {detail.premium_until ? fmtDate(detail.premium_until) : "—"}
                             {!detail.is_admin && (
                               <button onClick={() => setPremiumModal({
                                 current_until: detail.premium_until,
                                 // Date picker needs YYYY-MM-DD; slice off the ISO time portion.
                                 new_until: (detail.premium_until || "").slice(0, 10),
                                 reason: "",
                               })}
                               className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#C5A880] hover:underline"
                               data-testid="edit-premium-open">
                                 <Pencil className="w-3 h-3" /> Alterar
                               </button>
                             )}
                           </span>
                         } />
                  <Field label="Registo" value={fmtDate(detail.created_at)} />
                  {detail.premium_override_reason && (
                    <Field label="Motivo (admin)" value={detail.premium_override_reason} />
                  )}
                  {detail.deactivated_at && <Field label="Desativado em" value={fmtDate(detail.deactivated_at)} />}
                  {detail.deactivated_from_email && <Field label="Email original" value={detail.deactivated_from_email} mono />}
                </div>

                {/* Bio & meta */}
                {(detail.bio || detail.location) && (
                  <div className="text-sm">
                    <span className="label-uppercase block mb-2">Perfil público</span>
                    {detail.location && <p className="text-[#A29F9B] mb-1">{detail.location}</p>}
                    {detail.bio && <p className="text-[#DCD7D2] whitespace-pre-wrap leading-relaxed">{detail.bio}</p>}
                  </div>
                )}

                {/* Invoices */}
                <div>
                  <span className="label-uppercase block mb-2">Histórico de faturas/recibos</span>
                  {!invoices || invoices.length === 0 ? (
                    <p className="text-sm text-[#A29F9B]">Sem faturas emitidas.</p>
                  ) : (
                    <div className="border border-white/5 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="text-[#A29F9B]">
                          <tr>
                            <th className="text-left px-3 py-2">Número</th>
                            <th className="text-left px-3 py-2">Data</th>
                            <th className="text-left px-3 py-2">Descrição</th>
                            <th className="text-right px-3 py-2">Valor</th>
                            <th className="text-left px-3 py-2">Link</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((inv, i) => (
                            <tr key={inv.invoice_id || inv.id || i} className="border-t border-white/5">
                              <td className="px-3 py-2 font-mono text-[#DCD7D2]">{inv.document_number || inv.invoice_id || "—"}</td>
                              <td className="px-3 py-2 text-[#A29F9B]">{fmtDate(inv.issued_at || inv.date)}</td>
                              <td className="px-3 py-2 text-[#DCD7D2]">{inv.package_id || inv.description || "—"}</td>
                              <td className="px-3 py-2 text-right text-[#C5A880]">{fmtMoney(inv.amount || inv.total || 0, inv.currency || "EUR")}</td>
                              <td className="px-3 py-2">
                                {inv.pdf_url ? <a href={inv.pdf_url} target="_blank" rel="noreferrer" className="text-[#C5A880] hover:underline inline-flex items-center gap-1"><FileText className="w-3 h-3"/>PDF</a> : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!detail.is_admin && (
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-white/5">
                    {detail.profile_state === "N" ? (
                      <button onClick={() => changeState("S")} disabled={acting}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm disabled:opacity-50"
                        data-testid="admin-reactivate">
                        {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Reativar conta
                      </button>
                    ) : (
                      <button onClick={() => changeState("N")} disabled={acting}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7A2021] hover:bg-[#8B2525] text-white text-sm disabled:opacity-50"
                        data-testid="admin-deactivate">
                        {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
                        Desativar conta
                      </button>
                    )}
                    <button onClick={() => setPurgeModal({ confirmText: "", keepInvoices: true })}
                      disabled={acting}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#7A2021] text-[#7A2021] hover:bg-[#7A2021] hover:text-white text-sm transition-colors ml-auto disabled:opacity-50"
                      data-testid="admin-purge-open">
                      <Trash2 className="w-4 h-4" /> Eliminar permanentemente
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Purge confirmation modal (nested) */}
      {purgeModal && detail && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="purge-modal">
          <div className="bg-[#161920] border-2 border-[#7A2021]/50 rounded-2xl max-w-lg w-full p-7">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 rounded-full bg-[#7A2021]/20 border border-[#7A2021]/50 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-[#7A2021]" />
              </div>
              <div>
                <h3 className="font-display text-xl mb-1">Eliminar permanentemente</h3>
                <p className="text-sm text-[#DCD7D2] leading-relaxed">
                  Vais apagar <strong className="text-[#F5F2EE]">todos os dados</strong> deste utilizador.
                  Esta ação <strong className="text-[#7A2021]">não pode ser revertida</strong>.
                </p>
              </div>
            </div>

            <ul className="text-xs text-[#A29F9B] space-y-1.5 mb-5 list-disc list-inside bg-[#0C0E12] border border-white/5 rounded-lg p-4">
              <li>Perfil, fotos, questionário, bio, preferências</li>
              <li>Todas as ligações (matches) e mensagens associadas</li>
              <li>Likes / passes / admirações recebidas ou dadas</li>
              <li>Diário de encontros, notificações, visitas ao perfil</li>
              <li>Análises IA em cache e tokens de password reset</li>
            </ul>

            <label className="flex items-start gap-2 mb-4 text-sm text-[#DCD7D2] cursor-pointer">
              <input type="checkbox" checked={purgeModal.keepInvoices}
                onChange={(e) => setPurgeModal({ ...purgeModal, keepInvoices: e.target.checked })}
                className="mt-1 accent-[#C5A880]" data-testid="purge-keep-invoices" />
              <span>
                Manter faturas/recibos (anonimizados) <span className="text-xs text-[#A29F9B]">— recomendado para obrigação fiscal (10 anos)</span>
              </span>
            </label>

            <p className="text-xs text-[#A29F9B] mb-2">
              Para confirmar, cola o ID do utilizador abaixo:
            </p>
            <code className="block text-xs font-mono bg-[#0C0E12] border border-white/5 rounded px-2 py-1 mb-3 break-all">{detail.id}</code>
            <input type="text" value={purgeModal.confirmText}
              onChange={(e) => setPurgeModal({ ...purgeModal, confirmText: e.target.value })}
              placeholder="Cola o ID aqui..."
              className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2.5 text-sm font-mono focus:border-[#7A2021]/60 focus:outline-none mb-5"
              data-testid="purge-confirm-input" autoFocus />

            <div className="flex gap-2 justify-end">
              <button onClick={() => setPurgeModal(null)} disabled={acting}
                className="btn-secondary text-sm" data-testid="purge-cancel">Cancelar</button>
              <button onClick={executePurge}
                disabled={acting || purgeModal.confirmText !== detail.id}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7A2021] hover:bg-[#8B2525] text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="purge-confirm">
                {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {acting ? "A eliminar..." : "Eliminar agora"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium override modal */}
      {premiumModal && detail && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
             data-testid="premium-modal" onClick={() => setPremiumModal(null)}>
          <div className="bg-[#161920] border border-[#C5A880]/40 rounded-2xl max-w-md w-full p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/40 flex items-center justify-center shrink-0">
                <Crown className="w-4 h-4 text-[#C5A880]" />
              </div>
              <div>
                <h3 className="font-display text-xl text-[#F5F2EE] mb-1">Alterar premium</h3>
                <p className="text-sm text-[#A29F9B] leading-relaxed">
                  Define a data até à qual <strong className="text-[#F5F2EE]">{detail.name || detail.email}</strong> tem acesso Premium. Deixa em branco para revogar.
                </p>
              </div>
            </div>

            <label className="block text-xs uppercase tracking-wider text-[#A29F9B] mb-1">Válido até (inclusive)</label>
            <input type="date"
                   value={premiumModal.new_until || ""}
                   onChange={(e) => setPremiumModal((m) => ({ ...m, new_until: e.target.value }))}
                   className="w-full px-3 py-2 rounded-lg bg-[#0C0E12] border border-[#A29F9B]/40 focus:border-[#C5A880] outline-none text-[#F5F2EE] mb-3"
                   data-testid="premium-date-input" />

            {/* Quick-add presets — common admin operations in one click */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {[
                { label: "+1 mês", days: 30 },
                { label: "+3 meses", days: 90 },
                { label: "+6 meses", days: 180 },
                { label: "+1 ano", days: 365 },
              ].map((p) => (
                <button key={p.label} type="button" onClick={() => {
                  const base = premiumModal.new_until ? new Date(premiumModal.new_until) : new Date();
                  base.setDate(base.getDate() + p.days);
                  setPremiumModal((m) => ({ ...m, new_until: base.toISOString().slice(0, 10) }));
                }}
                className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-[#A29F9B] hover:border-[#C5A880]/40 hover:text-[#C5A880] transition-colors"
                data-testid={`premium-preset-${p.days}`}>
                  {p.label}
                </button>
              ))}
              <button type="button" onClick={() => setPremiumModal((m) => ({ ...m, new_until: "" }))}
                className="text-[11px] px-2.5 py-1 rounded-full border border-[#7A2021]/40 text-[#7A2021] hover:bg-[#7A2021]/10 transition-colors ml-auto"
                data-testid="premium-preset-revoke">
                Revogar
              </button>
            </div>

            <label className="block text-xs uppercase tracking-wider text-[#A29F9B] mb-1">Justificação (auditoria)</label>
            <input type="text" value={premiumModal.reason}
                   onChange={(e) => setPremiumModal((m) => ({ ...m, reason: e.target.value }))}
                   placeholder="Ex.: cliente VIP, compensação por downtime, etc."
                   className="w-full px-3 py-2 rounded-lg bg-[#0C0E12] border border-[#A29F9B]/40 focus:border-[#C5A880] outline-none text-[#F5F2EE] mb-4"
                   data-testid="premium-reason-input" />

            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setPremiumModal(null)} disabled={acting}
                className="text-sm text-[#A29F9B] hover:text-[#F5F2EE] px-3 py-2"
                data-testid="premium-cancel">Cancelar</button>
              <button onClick={savePremium} disabled={acting}
                className="text-sm inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C5A880] text-[#0C0E12] hover:bg-[#D4B690] disabled:opacity-50"
                data-testid="premium-save">
                {acting && <Loader2 className="w-3 h-3 animate-spin" />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-xs text-[#A29F9B] mb-1">{label}</div>
    <div className={`text-[#DCD7D2] ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
  </div>
);

export default AdminUsers;
